import { InputChipOption } from '@farmshare/ui-components';
import {
  animalSpeciesHelper,
  capitalize,
  formatToCurrency,
  inspectionLevelHelper,
  RecursivePartial,
} from '@farmshare/utils';
import { FormikProps } from 'formik';
import { sortBy } from 'lodash';
import { useMemo } from 'react';

import {
  EnumCutsheetAnimalSpecies,
  ProcessorCapability,
  ProcessorSettings,
  ViewProcessorAdminQueryResult,
} from 'lib/graphql';

import {
  ExtrasModel,
  TrimModel,
} from 'pages/processor/capabilities/_views/extras-trim-section';

import { CutsheetFormData } from './types';

export const formatInputChipLabel = (item: ExtrasModel | TrimModel) => {
  if ('type' in item && item.type === 'flatFee') {
    return `${capitalize(item.name)} - ${formatToCurrency(item.fee)}`;
  }
  if (
    'trimSelection' in item &&
    item.trimSelection === 'allowSelectionByPercentage'
  ) {
    return capitalize(item.name);
  }

  return (
    `${capitalize(item.name)} / ${formatToCurrency(item.pricePerPound)}/lb` +
    ((item.minLbs ?? 0) > 0 ? ` / ${item.minLbs}lb min` : '')
  );
};

export const useCutsheetAnimalSpeciesOptions = (
  settings?: ProcessorSettings,
) => {
  return useMemo(() => {
    return settings?.animalSettings?.reduce(
      (acc: InputChipOption[], animalSetting) => {
        if (animalSetting?.isEnabled) {
          const speciesOption = animalSpeciesHelper(animalSetting.species);

          acc.push({ ...speciesOption, sortValue: speciesOption.order });
        }
        return acc;
      },
      [],
    );
  }, [settings?.animalSettings]);
};

export const useCutsheetInspectionLevelOptions = (
  fP: FormikProps<CutsheetFormData>,
  settings?: ProcessorSettings,
) =>
  useMemo(() => {
    const animalSetting = settings?.animalSettings?.find(
      (o) =>
        o?.species.toString() ===
        (
          fP.values.animalSpecies ??
          settings?.animalSettings?.[0]?.species ??
          ''
        ).toString(),
    );

    if (!animalSetting) {
      return [];
    }

    return sortBy(
      animalSetting?.inspectionLevels?.map((inspectionLevel) => {
        const inspectionOption = inspectionLevelHelper(inspectionLevel ?? '');
        return { ...inspectionOption, sortValue: inspectionOption.order };
      }),
      'sortValue',
    );
  }, [settings?.animalSettings, fP.values.animalSpecies]);

export const useCutsheetProcessorCapabilities = (
  fP: FormikProps<CutsheetFormData>,
  capabilities: RecursivePartial<ProcessorCapability>[],
) =>
  useMemo(
    () =>
      capabilities.find((capability) => {
        return (
          (capability.animalSpecies as unknown as EnumCutsheetAnimalSpecies) ===
            fP.values.animalSpecies &&
          capability.inspectionLevel === fP.values.inspectionLevel
        );
      }),
    [fP.values.animalSpecies, fP.values.inspectionLevel, capabilities],
  );
